import { useMemo } from 'react';
import { useAppSelector } from '@/app/hooks';
import { useAppFlags } from '@/hooks/useAppFlags';
import type { GroupId } from '@/features/types';
import {
  GroupAnalysisType,
  type NetworkGroupData as NetworkGroup,
} from '@/generated/types/navigator/types.gen';

export interface ShouldShowActionsParams {
  selectedAnalysis: GroupAnalysisType;
  selectedNetworkGroup: NetworkGroup | null;
  spanNetworkGroup: NetworkGroup | null;
  circuitNetworkGroupId: GroupId | null;
  flagCircuitExport: boolean;
  activeGroupName: string | null;
}

/**
 * Determines whether bulk actions should be shown based on analysis type,
 * network group selection, and feature flags.
 * Exported for testing; use useShouldShowBulkActions hook in components.
 *
 * @param params - The parameters required to determine if actions should be shown
 * @returns true if bulk actions should be displayed, false otherwise
 */
export function shouldShowActions(params: ShouldShowActionsParams): boolean {
  const {
    selectedAnalysis,
    selectedNetworkGroup,
    spanNetworkGroup,
    circuitNetworkGroupId,
    flagCircuitExport,
    activeGroupName,
  } = params;

  // If there's an active group name, this means a group is selected
  // In this case, show actions
  if (activeGroupName !== null) {
    return true;
  }

  if (selectedAnalysis === GroupAnalysisType.CLEAR) {
    return false;
  }

  // We have a special case for allowing exports:
  // https://20treeai.slack.com/archives/C06EGKVQC3G/p1758574104526509?thread_ts=1758572487.017969&cid=C06EGKVQC3G
  if (selectedNetworkGroup?.id === circuitNetworkGroupId) {
    return (
      flagCircuitExport &&
      selectedAnalysis === GroupAnalysisType.WORKLOAD_ANALYSIS
    );
  }

  return selectedNetworkGroup?.id === spanNetworkGroup?.id;
}

/**
 * Hook that calls shouldShowActions to determine whether bulk actions should be shown.
 * Handles Redux state fetching and feature flag resolution internally.
 */
export function useShouldShowBulkActions(): boolean {
  const { flagCircuitExport } = useAppFlags();

  const {
    selectedAnalysis,
    selectedNetworkGroup,
    spanNetworkGroup,
    circuitNetworkGroupId,
    activeGroupName,
  } = useAppSelector((s) => s.analysisMap);

  return useMemo(
    () =>
      shouldShowActions({
        selectedAnalysis,
        selectedNetworkGroup,
        spanNetworkGroup,
        circuitNetworkGroupId,
        flagCircuitExport: flagCircuitExport,
        activeGroupName,
      }),
    [
      selectedAnalysis,
      selectedNetworkGroup,
      spanNetworkGroup,
      circuitNetworkGroupId,
      flagCircuitExport,
      activeGroupName,
    ]
  );
}
